import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {Search} from "lucide-react";

import VideoStats from "../components/VideoStats";
import VideoGrid from "../components/VideoGrid";
import CatalogBrowser from "../components/CatalogBrowser";
import VideoModal from "../components/VideoModal";
import {buildCatalog} from "../catalog/buildCatalog";
import OrbitLoader from "../../../ui/components/OrbitLoader";
import useActivity from "../hooks/useActivity";
import useWatchHistory from "../hooks/useWatchHistory";
import {useStorage} from "../hooks/useStorage";
import {useSync} from "../hooks/useSync";
import {useVideos} from "../hooks/useVideos";

const VideoDashboard = ({
  user,
  recentVideos,
  filteredQuery,
  onWatchHistoryChange,
}) => {
  // The single useStorage instance for the dashboard. useActivity and
  // useWatchHistory each used to call useStorage themselves; the two
  // instances shared one sessionStorage key and each wrote the full
  // {activity, watchHistory} object, so whichever debounced write fired
  // last clobbered the other slice with a stale copy.
  const {activity, watchHistory, setActivity, setWatchHistory} = useStorage({
    user,
  });
  const {activityRef} = useActivity({user, activity, setActivity});
  const {historyRef} = useWatchHistory({user, watchHistory, setWatchHistory});
  const {videos, filtered, isLoading} = useVideos({
    filteredQuery,
    watchHistory,
  });

  // Section -> Playlist -> ordered videos, derived from the catalog rows.
  const catalog = useMemo(() => buildCatalog(videos), [videos]);
  const {updateDailyStat, handleVideoProgress} = useSync({
    user,
    activity,
    activityRef,
    setActivity,
    watchHistory,
    historyRef,
    setWatchHistory,
  });

  // The URL is the source of truth for the player and the playlist detail
  // view: `?v=<id>` = which video the player has open, `?playlist=<id>` =
  // which playlist detail is showing. Opening/switching/closing all navigate,
  // so every state is a shareable permalink, refresh restores it, and the
  // browser Back button walks player -> previous video -> browse view,
  // instead of leaving the app.
  //
  // This component is the ONLY writer of the query string. CatalogBrowser
  // used to call setSearchParams for `playlist` itself, off its own
  // `searchParams` snapshot; when its cleanup and the `v` cleanup here
  // landed in the same tick, each wrote a full param set built from the same
  // pre-update snapshot and the last write silently resurrected the other's
  // deletion (self-correcting next render, but with junk history entries).
  // One writer, one URLSearchParams per update, no clobbering.
  const [searchParams, setSearchParams] = useSearchParams();
  const vParam = searchParams.get("v");
  const playlistParam = searchParams.get("playlist");

  // Resolve ?v= against the catalog. Ids arrive from SMS / hand-built links,
  // so tolerate copy-paste noise (whitespace, quotes) and casing.
  const openVideo = useMemo(() => {
    if (!vParam) return null;
    const wanted = vParam
      .trim()
      .replace(/^["']|["']$/g, "")
      .toLowerCase();
    return (
      videos.find((x) => String(x.id ?? "").toLowerCase() === wanted) || null
    );
  }, [vParam, videos]);

  // Resolve ?playlist= against the catalog, across all sections (a deep-link
  // can point at any of them). Owned here, not in CatalogBrowser, so both
  // params have a single resolver and a single writer.
  const playlists = useMemo(
    () => catalog.sections.flatMap((s) => s.playlists),
    [catalog],
  );
  const openPlaylist = useMemo(
    () =>
      (playlistParam && playlists.find((p) => p.id === playlistParam)) || null,
    [playlistParam, playlists],
  );

  // The ordered list the player's "Up next" advances through. Preference
  // order: the playlist open in the URL (when it really contains the video),
  // the playlist that owns the video, the intro row, a section's loose-video
  // group. A video in none of these plays without a queue.
  const queue = useMemo(() => {
    if (!openVideo) return undefined;
    const has = (list) => list.some((x) => x.id === openVideo.id);
    if (openPlaylist && has(openPlaylist.videos)) return openPlaylist.videos;
    const owner = playlists.find((p) => has(p.videos));
    if (owner) return owner.videos;
    if (has(catalog.intro)) return catalog.intro;
    const looseOwner = catalog.sections.find((s) => has(s.looseVideos));
    return looseOwner?.looseVideos;
  }, [openVideo, openPlaylist, playlists, catalog]);

  // Open/switch push a history entry (Back = previous video, then the
  // pre-player view). Close also pushes, so Back after closing can reopen --
  // same contract as leaving any page.
  const openVideoInPlayer = useCallback(
    (video) => {
      const next = new URLSearchParams(searchParams);
      next.set("v", String(video.id ?? video.videoId));
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );
  const closePlayer = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("v");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Entering/leaving the playlist detail view are navigations (push), so
  // Back walks out of the detail view naturally. CatalogBrowser reports the
  // tapped playlist up and these write the URL, mirroring how the cards
  // report the tapped video up to openVideoInPlayer.
  const openPlaylistDetail = useCallback(
    (id) => {
      const next = new URLSearchParams(searchParams);
      next.set("playlist", id);
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );
  const closePlaylistDetail = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("playlist");
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // Params that don't resolve once the catalog is in (bad/stale SMS link, a
  // playlist that fell under the size threshold) must not sit in the URL
  // looking meaningful -- drop them silently and show what's left. Both are
  // cleaned in ONE update so a stale `v` and a stale `playlist` arriving
  // together can't overwrite each other's deletion. replace: a junk URL
  // isn't a history state worth keeping.
  useEffect(() => {
    if (isLoading || videos.length === 0) return;
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (vParam && !openVideo) {
      next.delete("v");
      changed = true;
    }
    if (playlistParam && !openPlaylist) {
      next.delete("playlist");
      changed = true;
    }
    if (changed) setSearchParams(next, {replace: true});
  }, [
    vParam,
    playlistParam,
    isLoading,
    videos,
    openVideo,
    openPlaylist,
    searchParams,
    setSearchParams,
  ]);

  // Round-3 #7: Recently Watched lived on App but only sourced from a
  // snapshot of `loginState.user.watch_history` taken at login. Plays
  // never reached App, so the sidebar stayed frozen. We forward live
  // watchHistory updates back up so App can recompute the sidebar in
  // a single useMemo.
  useEffect(() => {
    if (!onWatchHistoryChange) return;
    onWatchHistoryChange(watchHistory);
  }, [watchHistory, onWatchHistoryChange]);

  // Round-4: the search box lives in the top header, but the matching
  // videos render in the grid far below the fold (banner + stats +
  // sort + filter sit above it). A user searching from the top saw no
  // change and assumed nothing matched. When a search term is active,
  // scroll the results section into view so the feedback is immediate.
  const activeQuery = (filteredQuery || "").trim();
  // True while CatalogBrowser shows a "See all" playlist detail; the header
  // (tagline + progress card) hides so "Back" sits at the top of the page.
  const [playlistViewOpen, setPlaylistViewOpen] = useState(false);
  const resultsRef = useRef(null);
  // The grid is virtualized against this element, so it needs a ref to it.
  const scrollContainerRef = useRef(null);
  useEffect(() => {
    if (!activeQuery) return;
    resultsRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
  }, [activeQuery]);

  // Entering the playlist detail view must land at the very top. The snap in
  // CatalogBrowser fires while the stats header is still rendered (this
  // component hides it one render later, via playlistViewOpen), so the
  // content shifts up by the header's height after the snap and the view
  // ends up scrolled ~a header-height into the playlist -- most visible on
  // a ?playlist= deep-link cold load (QA: quarter-screen down on iPhone).
  // Re-zeroing here runs after the header is actually gone.
  useEffect(() => {
    if (playlistViewOpen) scrollContainerRef.current?.scrollTo(0, 0);
  }, [playlistViewOpen]);

  // While the catalog is still loading, show a clean blank screen with
  // just the spinner instead of rendering the dashboard chrome over an
  // empty grid. Keeps the initial load from looking broken/janky.
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-white">
        <OrbitLoader />
      </div>
    );
  }

  return (
    /* This div -- not the outer <main> -- is the element that actually
       scrolls the learner grid, so overscroll-contain belongs here: it
       stops the scroll leaking to the page and the mobile rubber-band
       bounce at the top/bottom of the (very long) video list. It is also
       the scroll container the virtualized VideoGrid measures against. */
    <div
      ref={scrollContainerRef}
      className="h-full w-full p-4 md:p-8 pb-[calc(4rem+env(safe-area-inset-bottom))] font-sans bg-white overflow-y-auto overscroll-contain relative"
    >
      {/* THE player. One modal, URL-driven, rendered here so it opens
          identically in search mode, browse mode, playlist detail, from the
          Recently Watched sidebar, and from a cold ?v= deep-link. onProgress
          goes through the same useSync path as every play always has. */}
      {openVideo && (
        <VideoModal
          video={openVideo}
          queue={queue}
          watchHistory={watchHistory}
          onClose={closePlayer}
          recentVideos={recentVideos}
          onProgress={handleVideoProgress}
          onPlay={() => {}}
          onSwitchVideo={openVideoInPlayer}
        />
      )}

      {!playlistViewOpen && <VideoStats {...{activity, watchHistory}} />}

      <section ref={resultsRef} className="mb-6 scroll-mt-6">
        {activeQuery ? (
          <>
            {/* Search: a flat result grid across the whole catalog, with
                explicit feedback on the active term and match count. */}
            <div className="mb-4 flex items-center gap-2 text-sm text-slate-600">
              <Search size={16} className="text-blue-500 shrink-0" />
              <span>
                {filtered.length === 0 ? (
                  <>
                    No videos match{" "}
                    <span className="font-semibold text-slate-800">
                      &ldquo;{activeQuery}&rdquo;
                    </span>
                  </>
                ) : (
                  <>
                    Showing{" "}
                    <span className="font-semibold text-slate-800">
                      {filtered.length}
                    </span>{" "}
                    {filtered.length === 1 ? "result" : "results"} for{" "}
                    <span className="font-semibold text-slate-800">
                      &ldquo;{activeQuery}&rdquo;
                    </span>
                  </>
                )}
              </span>
            </div>
            <VideoGrid
              videos={filtered}
              scrollRef={scrollContainerRef}
              onOpenVideo={openVideoInPlayer}
              {...{watchHistory, updateDailyStat}}
            />
          </>
        ) : (
          // Browse: Section -> Playlist -> ordered videos.
          <CatalogBrowser
            catalog={catalog}
            openPlaylistId={playlistParam}
            onOpenPlaylist={openPlaylistDetail}
            onClosePlaylist={closePlaylistDetail}
            onSeeAllActiveChange={setPlaylistViewOpen}
            onOpenVideo={openVideoInPlayer}
            {...{watchHistory, updateDailyStat}}
          />
        )}
      </section>
    </div>
  );
};

export default VideoDashboard;
